import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import {
  saveConversationMessage,
  getConversationHistory,
  isReturningToSquare
} from './conversationMemory';
import { detectCrisisLevel, getCrisisResponse, logCrisisIncident } from './utils/crisisDetection';
import CrisisAlert from './components/CrisisAlert';

export default function ConversationFlow({ theme, squareNumber, userProfile, onClose }) {
  const { currentUser } = useAuth();
  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const [messages, setMessages]             = useState([]);
  const [userInput, setUserInput]           = useState('');
  const [isLoading, setIsLoading]           = useState(false);
  const [conversationStage, setConversationStage] = useState('initial');
  const [currentMood, setCurrentMood]       = useState('neutral');
  const [hasStarted, setHasStarted]         = useState(false);
  const [isReturning, setIsReturning]       = useState(false);
  const [previousMessages, setPreviousMessages] = useState([]);
  const [crisisDetected, setCrisisDetected] = useState(null);
  const [mounted, setMounted]               = useState(false);
  const [practiceCard, setPracticeCard]     = useState(null);

  const WORKER_URL = 'https://mokshapatra-api-proxy.pallavsinha8.workers.dev';

  useEffect(() => {
    setMounted(true);
    if (currentUser && squareNumber) loadConversationHistory();
  }, [currentUser, squareNumber]);

  useEffect(() => {
    const el = document.getElementById('msgs');
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const loadConversationHistory = async () => {
    try {
      const returning = await isReturningToSquare(currentUser.uid, squareNumber);
      setIsReturning(returning);
      if (returning) {
        const history = await getConversationHistory(currentUser.uid, squareNumber);
        setPreviousMessages(history);
      }
    } catch (err) {
      console.error('History load error:', err);
    }
  };

  const analyzeMood = (text) => {
    const t = text.toLowerCase();
    if (t.includes('scared') || t.includes('afraid') || t.includes('fear'))      return 'fearful';
    if (t.includes('tired') || t.includes('exhausted') || t.includes('drained'))  return 'depleted';
    if (t.includes('angry') || t.includes('frustrated') || t.includes('annoyed')) return 'angry';
    if (t.includes('sad') || t.includes('down') || t.includes('depressed'))       return 'sad';
    if (t.includes('hopeless') || t.includes('stuck') || t.includes('overwhelmed')) return 'overwhelmed';
    if (t.includes('excited') || t.includes('good') || t.includes('great'))       return 'positive';
    return 'neutral';
  };

  const getMoodContext = (mood) => ({
    fearful:     'User is feeling fearful. Be gentle and reassuring.',
    depleted:    'User is drained. Keep questions simple, acknowledge tiredness.',
    angry:       'User is frustrated. Validate their feelings, give space.',
    sad:         'User is down. Hold space with compassion, do not try to fix.',
    overwhelmed: 'User is overwhelmed. Simplify, offer grounding.',
    positive:    'User is in good spirits. Great time for deeper exploration.',
    neutral:     'User seems open and ready.',
  }[mood] ?? 'User seems open and ready.');

  const getSystemPrompt = () => {
    let contextNote = '';
    if (isReturning && previousMessages.length > 0) {
      contextNote = `\nCONTEXT: This user has visited this square before. Acknowledge their return naturally.`;
    }

    return `You are a compassionate spiritual guide on the Mokshapatra journey.

CURRENT PRACTICE:
Square ${squareNumber}: "${theme.title}"
${theme.desc}

MOOD DETECTION — read their responses carefully:
- Short responses (1–2 sentences) or negative/tired words → overwhelmed or drained. Ask simpler, warmer questions. Move toward practice in 1–2 more exchanges.
- Long, exploratory responses → engaged and open. Go deeper, 3–4 exchanges before practice.
- Deflective or one-word answers → gentle, use reflection instead of questions.
- Emotional or vulnerable → hold space. Do not rush to solutions.
Current detected mood context: ${getMoodContext(currentMood)}
${contextNote}

YOUR ROLE:
- Ask ONE question at a time, under 80 words
- Use their own words back to them
- Be curious and warm, not advisory
- Hold space — do not fix or give advice
- No therapy jargon

WHEN TO TRANSITION TO PRACTICE:
You decide when the user is ready — not after a fixed count. When you sense they have reflected enough, deliver the practice naturally. Start that message with exactly "Your practice for today:" followed by concrete step-by-step 2-minute instructions for "${theme.title}". After delivering the practice, do NOT ask another question. End with one sentence of encouragement only.`;
  };

  const callClaudeAPI = async (history) => {
    const response = await fetch(WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: history, systemPrompt: getSystemPrompt() }),
    });
    if (!response.ok) throw new Error(`API ${response.status}`);
    const data = await response.json();
    return data.content[0].text;
  };

  const handleStartConversation = async () => {
    setHasStarted(true);
    setIsLoading(true);
    try {
      const openingPrompt = `I just rolled Square ${squareNumber}: "${theme.title}". The practice is: ${theme.desc}. Ask me one warm opening question about how I'm feeling about this practice.${isReturning ? ' I have visited this square before — acknowledge my return naturally.' : ''}`;
      const reply = await callClaudeAPI([{ role: 'user', content: openingPrompt }]);
      if (!isMounted.current) return;
      const msg = { role: 'assistant', content: reply, timestamp: new Date().toISOString() };
      setMessages([msg]);
      setConversationStage('exploring');
      if (currentUser) await saveConversationMessage(currentUser.uid, squareNumber, msg);
    } catch (err) {
      console.error('Start error:', err);
      if (!isMounted.current) return;
      const fallback = { role: 'assistant', content: `How are you feeling about today's practice: ${theme.title}?`, timestamp: new Date().toISOString() };
      setMessages([fallback]);
      if (currentUser) await saveConversationMessage(currentUser.uid, squareNumber, fallback);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!userInput.trim()) return;
    const userText = userInput.trim();

    // Crisis check
    const crisis = detectCrisisLevel(userText);
    if (crisis.level !== 'none') {
      const resp = getCrisisResponse(crisis.level);
      setCrisisDetected({ level: crisis.level, response: resp });
      if (currentUser) await logCrisisIncident(currentUser.uid, { level: crisis.level, message: userText, matchedKeywords: crisis.matchedKeywords, squareNumber });
      if (crisis.level === 'tier2') return;
    }

    setCurrentMood(analyzeMood(userText));
    const userMsg = { role: 'user', content: userText, timestamp: new Date().toISOString() };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setUserInput('');
    setIsLoading(true);
    if (currentUser) await saveConversationMessage(currentUser.uid, squareNumber, userMsg);

    try {
      const history = updatedMessages.map(m => ({ role: m.role, content: m.content }));
      const reply = await callClaudeAPI(history);
      if (!isMounted.current) return;

      const aiMsg = { role: 'assistant', content: reply, timestamp: new Date().toISOString() };
      setMessages([...updatedMessages, aiMsg]);
      if (currentUser) await saveConversationMessage(currentUser.uid, squareNumber, aiMsg);

      // AI signals practice transition with this phrase — no fixed message count
      if (!practiceCard && reply.includes('Your practice for today:')) {
        setConversationStage('practice-suggestion');
        setPracticeCard(reply);
      }
    } catch (err) {
      console.error('Send error:', err);
      if (!isMounted.current) return;
      const errMsg = { role: 'assistant', content: "I'm having trouble connecting. Can you tell me more about what you're experiencing?", timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, errMsg]);
      if (currentUser) await saveConversationMessage(currentUser.uid, squareNumber, errMsg);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      {crisisDetected && (
        <CrisisAlert
          level={crisisDetected.level}
          response={crisisDetected.response}
          onContinue={() => setCrisisDetected(null)}
          onGroundingComplete={() => setCrisisDetected(null)}
        />
      )}

      <div style={{ ...styles.container, opacity: mounted ? 1 : 0 }}>
        {/* Header */}
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={onClose}>← Back</button>
          <div style={styles.headerCenter}>
            <div style={styles.squareLabel}>Square {squareNumber}{isReturning ? ' · Returning' : ''}</div>
            <div style={styles.headerTitle}>{theme.title}</div>
          </div>
          <div style={{ width: 60 }} />
        </div>

        {!hasStarted ? (
          /* Pre-conversation */
          <div style={styles.preCenterWrap}>
            <div style={styles.preCard}>
              <div style={{ fontSize: '52px', marginBottom: '16px' }}>💬</div>
              <h3 style={styles.preTitle}>{isReturning ? 'Welcome back' : "Let's explore together"}</h3>
              <p style={styles.preText}>
                {isReturning
                  ? "You've been here before. Let's see what's different this time."
                  : "I'll ask a few questions to help you connect with this practice."}
              </p>
              <div style={styles.practiceBox}>
                <div style={styles.practiceBoxLabel}>Today's Practice</div>
                <div style={styles.practiceBoxText}>{theme.desc}</div>
              </div>
              <button style={{ ...styles.startBtn, opacity: isLoading ? 0.7 : 1 }} onClick={handleStartConversation} disabled={isLoading}>
                {isLoading ? 'Starting...' : isReturning ? 'Continue Exploring' : 'Begin Conversation'}
              </button>
            </div>
          </div>
        ) : (
          /* Active conversation */
          <div style={styles.conversationWrap}>
            {/* Messages */}
            <div id="msgs" style={styles.messagesArea}>
              {messages.map((msg, i) => (
                <div key={i} style={{ ...styles.msgRow, justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{ ...styles.bubble, ...(msg.role === 'user' ? styles.userBubble : styles.aiBubble) }}>
                    {msg.content}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div style={{ ...styles.msgRow, justifyContent: 'flex-start' }}>
                  <div style={{ ...styles.bubble, ...styles.aiBubble }}>
                    <div style={styles.dots}>
                      <span style={styles.dot} /><span style={{ ...styles.dot, animationDelay: '0.2s' }} /><span style={{ ...styles.dot, animationDelay: '0.4s' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Inline practice card */}
            {practiceCard && (
              <div style={styles.practiceCard}>
                <div style={styles.practiceCardLabel}>Your Practice</div>
                <p style={styles.practiceCardText}>{practiceCard}</p>
              </div>
            )}

            {/* Input */}
            <div style={styles.inputRow}>
              <textarea
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Share what's coming up for you..."
                style={styles.textarea}
                rows={3}
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!userInput.trim() || isLoading}
                style={{ ...styles.sendBtn, opacity: (!userInput.trim() || isLoading) ? 0.4 : 1 }}
              >
                Send
              </button>
            </div>

            {/* Complete button — always visible once started */}
            <button style={styles.completeBtn} onClick={onClose}>
              I've Completed the Practice
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes typing { 0%,100%{opacity:0.3;transform:translateY(0)} 50%{opacity:1;transform:translateY(-4px)} }
        @keyframes slideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </>
  );
}

const styles = {
  container: {
    minHeight: '100vh', background: '#0D0D2B',
    display: 'flex', flexDirection: 'column',
    fontFamily: '"Lato", sans-serif',
    transition: 'opacity 0.4s ease',
  },

  header: {
    background: '#1E1145', borderBottom: '1px solid rgba(201,146,10,0.2)',
    padding: '16px 20px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
  },
  backBtn: {
    background: 'none', border: '1px solid rgba(201,146,10,0.3)',
    borderRadius: '10px', padding: '8px 16px', fontSize: '13px',
    fontWeight: '600', color: '#C9920A', cursor: 'pointer',
  },
  headerCenter: { textAlign: 'center' },
  squareLabel: { fontSize: '12px', color: 'rgba(245,240,232,0.45)', marginBottom: '2px' },
  headerTitle: {
    fontFamily: '"Cinzel", serif', fontSize: '16px', fontWeight: '700',
    color: '#F5F0E8',
  },

  preCenterWrap: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 20px',
  },
  preCard: {
    background: '#1E1145', border: '1px solid rgba(201,146,10,0.25)',
    borderRadius: '20px', padding: '40px 28px',
    maxWidth: '480px', width: '100%', textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
    animation: 'slideUp 0.5s ease-out',
  },
  preTitle: {
    fontFamily: '"Cinzel", serif', fontSize: '20px', fontWeight: '700',
    color: '#F5F0E8', marginTop: 0, marginBottom: '12px',
  },
  preText: { fontSize: '14px', color: 'rgba(245,240,232,0.55)', lineHeight: '1.6', marginBottom: '24px' },
  practiceBox: {
    background: 'rgba(201,146,10,0.08)', border: '1px solid rgba(201,146,10,0.2)',
    borderRadius: '12px', padding: '16px', marginBottom: '24px', textAlign: 'left',
  },
  practiceBoxLabel: { fontSize: '11px', fontWeight: '700', color: '#C9920A', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' },
  practiceBoxText: { fontSize: '14px', color: 'rgba(245,240,232,0.75)', lineHeight: '1.5' },
  startBtn: {
    width: '100%', padding: '14px', background: '#C9920A',
    border: 'none', borderRadius: '12px', fontSize: '16px',
    fontWeight: '700', color: '#0D0D2B', cursor: 'pointer',
    fontFamily: '"Lato", sans-serif',
  },

  conversationWrap: {
    flex: 1, display: 'flex', flexDirection: 'column',
    maxWidth: '680px', width: '100%', margin: '0 auto',
    padding: '16px 16px 24px',
  },
  messagesArea: {
    flex: 1, overflowY: 'auto', padding: '8px 0 16px',
    display: 'flex', flexDirection: 'column', gap: '12px',
    minHeight: '200px', maxHeight: 'calc(100vh - 380px)',
  },
  msgRow: { display: 'flex', width: '100%' },
  bubble: {
    maxWidth: '78%', padding: '12px 16px',
    borderRadius: '18px', fontSize: '14px', lineHeight: '1.6', wordWrap: 'break-word',
  },
  userBubble: { background: '#C9920A', color: '#0D0D2B', fontWeight: '500', borderBottomRightRadius: '4px' },
  aiBubble: { background: '#1E1145', color: '#F5F0E8', border: '1px solid rgba(201,146,10,0.15)', borderBottomLeftRadius: '4px' },
  dots: { display: 'flex', gap: '5px', alignItems: 'center', padding: '2px 0' },
  dot: { width: '7px', height: '7px', borderRadius: '50%', background: 'rgba(245,240,232,0.4)', animation: 'typing 1.4s infinite ease-in-out' },

  practiceCard: {
    background: 'rgba(201,146,10,0.08)', border: '1px solid rgba(201,146,10,0.3)',
    borderRadius: '14px', padding: '16px 18px', marginBottom: '12px',
  },
  practiceCardLabel: { fontSize: '11px', fontWeight: '700', color: '#C9920A', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' },
  practiceCardText: { fontSize: '14px', color: 'rgba(245,240,232,0.8)', lineHeight: '1.6', margin: 0 },

  inputRow: { display: 'flex', gap: '10px', marginBottom: '12px' },
  textarea: {
    flex: 1, padding: '12px 14px', fontSize: '14px',
    border: '1px solid rgba(201,146,10,0.25)', borderRadius: '12px',
    background: 'rgba(255,255,255,0.05)', color: '#F5F0E8',
    fontFamily: '"Lato", sans-serif', resize: 'none', outline: 'none',
    lineHeight: '1.5',
  },
  sendBtn: {
    padding: '0 20px', background: '#C9920A',
    border: 'none', borderRadius: '12px', fontSize: '14px',
    fontWeight: '700', color: '#0D0D2B', cursor: 'pointer',
    whiteSpace: 'nowrap', fontFamily: '"Lato", sans-serif',
  },
  completeBtn: {
    width: '100%', padding: '14px',
    background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
    borderRadius: '12px', fontSize: '15px', fontWeight: '600',
    color: '#86efac', cursor: 'pointer', fontFamily: '"Lato", sans-serif',
  },
};
